//@ts-check
import { apiClient } from './client.js';

/**
 * @returns {Promise<import('@/lib/types.js').Branding>}
 */
export async function getBranding() {
  const { data } = await apiClient.get('/settings/branding');
  return /** @type {import('@/lib/types.js').Branding} */ (data.branding);
}

/**
 * @param {{ siteTitle?: string, siteSubtitle?: string }} body
 * @returns {Promise<import('@/lib/types.js').Branding>}
 */
export async function updateBranding(body) {
  const { data } = await apiClient.patch('/settings/branding', body);
  return /** @type {import('@/lib/types.js').Branding} */ (data.branding);
}

/**
 * @param {File} file
 * @returns {Promise<import('@/lib/types.js').Branding>}
 */
export async function uploadLogo(file) {
  const form = new FormData();
  form.append('logo', file);
  // Let the browser set multipart boundary (do not force Content-Type).
  const { data } = await apiClient.post('/settings/branding/logo', form, {
    headers: { 'Content-Type': undefined },
  });
  return /** @type {import('@/lib/types.js').Branding} */ (data.branding);
}

/**
 * @param {File} file
 * @returns {Promise<import('@/lib/types.js').Branding>}
 */
export async function uploadFavicon(file) {
  const form = new FormData();
  form.append('favicon', file);
  const { data } = await apiClient.post('/settings/branding/favicon', form, {
    headers: { 'Content-Type': undefined },
  });
  return /** @type {import('@/lib/types.js').Branding} */ (data.branding);
}

/**
 * Public branding (login page / document title).
 * @returns {Promise<import('@/lib/types.js').Branding>}
 */
export async function getPublicBranding() {
  const { data } = await apiClient.get('/public/branding');
  return /** @type {import('@/lib/types.js').Branding} */ (data);
}

/**
 * @returns {Promise<{ enabled: boolean, months: number }>}
 */
export async function getRetentionSettings() {
  const { data } = await apiClient.get('/settings/retention');
  return data.settings;
}

/**
 * @param {{ enabled?: boolean, months?: number }} body
 */
export async function updateRetentionSettings(body) {
  const { data } = await apiClient.patch('/settings/retention', body);
  return data.settings;
}

/**
 * @returns {Promise<{
 *   videosToDelete: number,
 *   bytesToFree: number,
 *   cutoffDate: string,
 *   months: number,
 *   policyDays: number,
 *   byMarketplace: Array<{ marketplaceId: string|null, name: string, count: number, bytes: number }>,
 *   byDriveAccount: Array<{ driveAccountId: string|null, label: string, count: number, bytes: number }>,
 * }>}
 */
export async function previewRetention() {
  const { data } = await apiClient.get('/settings/retention/preview');
  return data.preview;
}

/**
 * @returns {Promise<{ deletedCount: number, freedBytes: number, skipped?: boolean, cutoffDate?: string }>}
 */
export async function runRetention() {
  const { data } = await apiClient.post('/settings/retention/run');
  return data;
}

/**
 * @returns {Promise<{ items: Array<{
 *   id: string,
 *   runBy: string|null,
 *   runByName: string,
 *   policyDays: number,
 *   deletedCount: number,
 *   freedBytes: number,
 *   runAt: string|Date,
 * }> }>}
 */
export async function getRetentionHistory() {
  const { data } = await apiClient.get('/settings/retention/history');
  return data;
}
